// layouts/certificaciones/services/certificaciones.js
import api from "services/api";

// Países por librería (importar SIEMPRE al tope)
import countries from "i18n-iso-countries";
import esCountries from "i18n-iso-countries/langs/es.json";

import { buildAndDownloadCertificatePdf } from "../generatePdf";

countries.registerLocale(esCountries);

/* ---------------- utils ---------------- */
const unwrapList = (data) =>
  Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.page)
    ? data.page
    : Array.isArray(data?.results)
    ? data.results
    : [];

const toIdName = (arr = []) =>
  (arr || []).map((o) => {
    const id = o.id ?? o._id ?? o.value ?? o.key ?? null;
    const name =
      o.name ?? o.nombre ?? o.title ?? o.label ?? o.code ??
      (typeof o === "string" ? o : id != null ? `#${id}` : "-");
    return { ...o, id, name };
  });

async function firstOk(paths = []) {
  for (const p of paths) {
    try {
      const { data } = await api.get(p);
      const list = unwrapList(data);
      if (Array.isArray(list)) return list;
    } catch {
      /* probar siguiente */
    }
  }
  return [];
}

// Helper para intentar múltiples endpoints de escritura (PATCH/POST/PUT)
async function tryWriteEndpoints(queue = []) {
  let lastErr;
  for (const step of queue) {
    const { method = "post", url, body, config } = step || {};
    try {
      const { data } = await api.request({
        method,
        url,
        data: body,
        ...(config || {}),
        headers: { "Content-Type": "application/json", ...(config?.headers || {}) },
      });
      return data?.data ?? data;
    } catch (e) {
      lastErr = e;
      // intenta el siguiente
    }
  }
  if (lastErr) throw lastErr;
  return null;
}


export function toCertFormData(fields = {}, files = {}) {
  const fd = new FormData();

  // Campos escalares
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;

    if (k === "genres" && Array.isArray(v)) {
      v.forEach((val) => fd.append("genres[]", String(val)));
      return;
    }
    if (k === "record_types" && Array.isArray(v)) {
      v.forEach((val) => fd.append("record_types[]", String(val)));
      return;
    }

    // el resto como string
    fd.append(k, String(v));
  });

  // Archivos: los nombres deben ser EXACTOS a lo que espera el backend
  if (files.label)  fd.append("label",  files.label);
  if (files.report) fd.append("report", files.report);
  if (files.cover)  fd.append("cover",  files.cover);

  return fd;
}


// ───────── Helpers: catálogo de grupos y sellos ─────────
async function fetchAllGroups() {
  try {
    const { data } = await api.get("/group");
    return Array.isArray(data?.data) ? data.data : Array.isArray(data?.page) ? data.page : Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
  } catch { return []; }
}
function buildGroupMap(list = []) {
  const map = {};
  (list || []).forEach((x) => {
    const key = String(x.id ?? x._id ?? x.value ?? x.key ?? "").trim();
    const name = x.name ?? x.nombre ?? x.title ?? x.label ?? "";
    if (key) map[key] = name || "";
  });
  return map;
}
async function resolveGroupNameFromCert(cert) {
  // embebido como objeto
  if (cert?.group && typeof cert.group === "object" && cert.group.name) return String(cert.group.name).trim();
  if (cert?.group_name) return String(cert.group_name).trim();

  const id = cert?.group_id ?? cert?.group;
  if (id == null || id === "") return "";

  const list = await fetchAllGroups();
  const map = buildGroupMap(list);
  return map[String(id)] || "";
}

async function fetchAllRecordLabels() {
  try {
    const { data } = await api.get("/recordLabel");
    return Array.isArray(data?.data) ? data.data : Array.isArray(data?.page) ? data.page : Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
  } catch {}
  try {
    const { data } = await api.get("/record-label");
    return Array.isArray(data?.data) ? data.data : Array.isArray(data?.page) ? data.page : Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
  } catch {}
  return [];
}
function buildRecordLabelMap(list = []) {
  const map = {};
  (list || []).forEach((x) => {
    const key = String(x.id ?? x._id ?? x.value ?? x.key ?? "").trim();
    const name = x.name ?? x.nombre ?? x.title ?? x.label ?? "";
    if (key) map[key] = name || "";
  });
  return map;
}
async function resolveLabelNameFromCert(cert) {
  if (cert?.record_label_name) return String(cert.record_label_name).trim();
  if (typeof cert?.record_label === "object" && cert.record_label?.name) return String(cert.record_label.name).trim();
  if (typeof cert?.record_label === "string" && !cert.record_label.endsWith(".pdf")) return String(cert.record_label).trim();

  const id = cert?.record_label_id ?? cert?.record_label;
  if (id == null || id === "") return "";

  const list = await fetchAllRecordLabels();
  const map = buildRecordLabelMap(list);
  return map[String(id)] || "";
}



/* ======================== CERTIFICACIONES (CRUD) ======================= */
const CERT_BASE = "/certified";

export async function listCertificaciones(params = null) {
  // Mapea limit/offset → __limit/__offset si vinieran así
  const p = { ...(params || {}) };

  // Soporta ambas formas: limit/offset o __limit/__offset
  if (p.limit != null)  { p.__limit  = p.limit;  delete p.limit;  }
  if (p.offset != null) { p.__offset = p.offset; delete p.offset; }

  const qs = Object.keys(p).length ? `?${new URLSearchParams(p).toString()}` : "";
  const { data } = await api.get(`${CERT_BASE}${qs}`);

  // La API regresa { total, page:[...], limit, offset }
  const rows   = Array.isArray(data?.page) ? data.page : unwrapList(data);
  const total  = Number(data?.total ?? rows.length);
  const limit  = Number(data?.limit  ?? p.__limit  ?? rows.length);
  const offset = Number(data?.offset ?? p.__offset ?? 0);

  return { rows, total, limit, offset };
}

export async function getCertificacion(id) {
  try {
    const { data } = await api.get(`${CERT_BASE}/${encodeURIComponent(id)}`);
    return data?.data ?? data ?? null;
  } catch (e) {
    if (e?.response?.status === 404 || e?.response?.status === 405) {
      const { data } = await api.get(CERT_BASE, { params: { id } });
      return data?.data ?? data ?? null;
    }
    throw e;
  }
}

export async function createCertificacion(body) {
  const isFD = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = isFD ? {} : { "Content-Type": "application/json" };
  const { data } = await api.post(CERT_BASE, body, { headers });
  return data?.data ?? data;
}

export async function updateCertificacion(id, body) {
  const isFD = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = isFD ? {} : { "Content-Type": "application/json" };
  const { data } = await api.put(`${CERT_BASE}/${encodeURIComponent(id)}`, body, { headers });
  return data?.data ?? data;
}

export async function deleteCertificacion(id) {
  const { data } = await api.delete(`${CERT_BASE}/${encodeURIComponent(id)}`);
  return data?.data ?? data;
}

/* ========= NUEVO: actualizar estatus y notificar decisión ========= */

export async function updateCertStatus(id, status, reason) {
  const url = `/certified/${encodeURIComponent(id)}`;
  const headers = { headers: { "Content-Type": "application/json" } };

  const send = async (pl) => {
    try {
      const { data } = await api.patch(url, pl, headers);
      return data?.data ?? data;
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404 || st === 405) {
        const { data } = await api.put(url, pl, headers);
        return data?.data ?? data;
      }
      throw e;
    }
  };

  try {
    return await send({ status, ...(reason ? { reason } : {}) });
  } catch (e) {
    const msg = e?.response?.data ?? e?.message ?? "";
    const s = typeof msg === "string" ? msg : JSON.stringify(msg);
    if (s.includes("must be boolean")) {
      const statusBool = Number(status) === 1;
      return await send({ status: statusBool, ...(reason ? { reason } : {}) });
    }
    throw e;
  }
}

export async function downloadCertPdf(id, filename = `certificado_${id}.pdf`) {
  // 1) Traer la certificación (con tus dos rutas)
  let cert = null;
  try {
    const { data } = await api.get(`/certified/${encodeURIComponent(id)}`);
    cert = data?.data ?? data ?? null;
  } catch (e) {
    if (e?.response?.status === 404 || e?.response?.status === 405) {
      const { data } = await api.get("/certified", { params: { id } });
      cert = data?.data ?? data ?? null;
    } else {
      throw e;
    }
  }
  if (!cert) throw new Error("No se encontró la certificación.");

  // 2) Validar estatus aceptado
  const st = typeof cert.status === "boolean" ? (cert.status ? 1 : 0) : Number(cert.status || 0);
  if (st !== 1) {
    const err = new Error("La certificación aún no está aceptada (403).");
    err.code = 403;
    throw err;
  }

  // 3) Resolver el nombre a mostrar: GRUPO primero, si no hay → SELLO
  let displayName = await resolveGroupNameFromCert(cert);
  if (!displayName) {
    displayName = await resolveLabelNameFromCert(cert);
  }
  // Evitar mostrar nombres que en realidad son archivos .pdf
  if (displayName && String(displayName).toLowerCase().endsWith(".pdf")) {
    displayName = "";
  }

  // 4) Nombre del archivo y construir PDF
  const safe = (s = "") => String(s).trim().replace(/\s+/g, "_");
  const finalName = filename || `certificado_${safe(cert.artist || "cert")}_${safe(cert.title || "")}.pdf`;

  await buildAndDownloadCertificatePdf(cert, finalName, displayName || "—");
}


export async function notifyCertDecision(id, body = {}) {
  const base = `/certified/${encodeURIComponent(id)}`;
  const headers = { headers: { "Content-Type": "application/json" } };

  const candidates = [
    `${base}/notify-decision`,
    `${base}/notifyDecision`,
    `${base}/notify_decision`,
    `${base}/notify`,
    `${base}/email`,
  ];

  for (const path of candidates) {
    try {
      const { data } = await api.post(path, body, headers);
      return data?.data ?? data ?? { ok: true };
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404 || st === 405 || st === 501) continue;
      throw e;
    }
  }
  return { ok: false, skipped: true, reason: "not_implemented" };
}

/* ====== catálogos (tus existentes) ====== */
export async function listFormatos()    { const { data } = await api.get("/format");       return unwrapList(data); }
export async function listPerformers()  { const { data } = await api.get("/performer");    return unwrapList(data); }
export async function listSellos(params = {}) {
  const { data } = await api.get("/recordLabel", {
    params,
  });

  return unwrapList(data);
}
export async function listGrupos(params = {}) {
  const { data } = await api.get("/group", {
    params,
  });

  return unwrapList(data);
}
export async function listGeneros()     { const { data } = await api.get("/genre");        return unwrapList(data); }
export async function listRecordTypes() {
  try {
    const { data } = await api.get("/recordType");
    return unwrapList(data);
  } catch {
    const { data } = await api.get("/record-type");
    return unwrapList(data);
  }
}

/* ======================== PAÍSES (librería) ======================= */
export function listPaises() {
  const names = countries.getNames("es", { select: "official" }) || {};
  return Object.entries(names)
    .map(([code, name]) => ({ id: code, code, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}
